import React, { FC, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { T, stringFormat } from 'src/shared/helpers';
import InspectorShell, { InspectorSubjectKind } from './inspectorShell';
import { SceneEditorAction } from './sceneEditorActionBar';

// IL PANNELLO DI UN MUCCHIO. Con più di una riga presa, la domanda «cosa sto guardando» ha una
// risposta al plurale, e il pannello che descrive un soggetto solo non sa darla: mostrava quello
// su cui era caduto l'ultimo clic, cioè una delle sette cose scelte, come se le altre sei non
// esistessero.
//
// DICE CHE COSA HAI PRESO, E COSA PUOI FARNE. Prima l'elenco — con l'icona di specie su ogni riga,
// perché un mucchio può essere fatto di cose diverse — poi i comandi che valgono per tutti.
//
// I COMANDI SONO GLI STESSI DELLA BARRA, non una seconda grammatica: qui hanno la parola scritta
// accanto al glifo perché c'è la larghezza per scriverla, e perché un comando che agisce su sette
// cose insieme è il momento sbagliato per farsi indovinare da un'icona. Un motore solo, due porte.

// DUE ELENCHI, DUE MESTIERI. Un MUCCHIO PRESO è un recinto: dice che cosa hai in mano adesso, e
// il filo intorno con i divisori fra le righe è ciò che lo rende una cosa sola invece di un
// elenco che continua. Il contenuto di un GRUPPO no: quelle righe si premono, e una gabbia
// intorno a delle porte le fa sembrare un riquadro di sola lettura — nel mockup infatti non c'è.
const List = styled.ul<{ $plain?: boolean }>`
    margin: 0 0 4px;
    border: ${(props) => (props.$plain ? 'none' : `1px solid ${props.theme.app.borderColor}`)};
    border-radius: 6px;
    padding: 0;
    max-height: 260px;
    overflow-y: auto;
    list-style: none;
`;

const Entry = styled.li<{ $plain?: boolean }>`
    color: ${(props) => props.theme.app.textColor};
    font-family: ${(props) => props.theme.app.font};
    font-size: 12px;

    & + & {
        border-top: ${(props) => (props.$plain ? 'none' : `1px solid ${props.theme.app.borderColor}`)};
    }
`;

// LA RIGA HA DUE FORME PERCHÉ HA DUE MESTIERI, e il disegno è lo stesso: dentro un mucchio
// preso dice soltanto che cosa hai in mano — non c'è dove andare, sono tutte lì — mentre dentro
// un gruppo è anche la PORTA per i suoi, e allora si preme. La forma la decide chi costruisce
// la riga, passando o non passando `onPick`.
const entryBody = css`
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    padding: 5px 8px;

    svg {
        flex: none;
        width: 14px;
        height: 14px;
    }
`;

const EntryStatic = styled.span`
    ${entryBody}
`;

const EntryPick = styled.button`
    ${entryBody}
    width: 100%;
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;

    &:hover {
        background: ${(props) => props.theme.app.component.button.primary.hover.backgroundColor};
    }

    &:focus-visible {
        outline: 2px solid ${(props) => props.theme.app.secondary};
        outline-offset: -2px;
    }
`;

const EntryName = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const Commands = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: 12px;
`;

const Command = styled.button<{ $danger?: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 6px;
    padding: 7px 10px;
    background: transparent;
    color: ${(props) => (props.$danger ? props.theme.app.component.button.destructive.default.backgroundColor : props.theme.app.textColor)};
    font-family: ${(props) => props.theme.app.font};
    font-size: 12px;
    /* A TUTTA COLONNA E AL CENTRO: un comando che prende la riga intera non ha un vicino da cui
       farsi allineare, e il testo appoggiato a sinistra di uno spazio vuoto lascia il bottone
       sbilanciato. È la forma che il mockup dà a «Remove group». */
    justify-content: center;
    cursor: pointer;

    svg {
        flex: none;
        width: 14px;
        height: 14px;
    }

    &:hover:not(:disabled) {
        border-color: ${(props) => props.theme.app.secondary};
    }

    &:disabled {
        opacity: ${(props) => props.theme.app.disabledOpacity};
        cursor: default;
    }
`;

export interface SelectionEntry {
    id: string;
    name: string;
    icon?: ReactNode;
    /** Presente: la riga è una porta, e premerla porta al suo soggetto. */
    onPick?: () => void;
}

export interface SelectionInspectorProps {
    entries: SelectionEntry[];
    /** Gli stessi comandi della barra: qui ricevono la selezione invece di una riga. */
    actions: SceneEditorAction[];
    /** Come si chiama, al plurale, ciò che è stato preso: «objects», «lights». */
    subjectLabel: string;
    /**
     * UN INSIEME CHE HA UN NOME PROPRIO — un gruppo di materiali — invece di un mucchio nato da
     * un clic. Cambia solo la testata: sotto, le due cose sono la stessa — un elenco di soggetti e
     * i comandi che valgono per tutti — e per questo non sono due componenti.
     */
    name?: string;
    kind?: InspectorSubjectKind;
    specs?: string;
    onRename?: (name: string) => void;
}

const SelectionInspector: FC<SelectionInspectorProps> = ({ entries, actions, subjectLabel, name, kind, specs, onRename }) => {
    // Basta che UNA riga sia una porta: un elenco mezzo premibile e mezzo no non esiste.
    const pickable = entries.some((entry) => entry.onPick);

    return (
        <InspectorShell
            kind={kind ?? 'selection'}
            name={name ?? stringFormat('{0} {1}', entries.length.toString(), subjectLabel)}
            specs={specs}
            onRename={onRename}
        >
            <List $plain={pickable}>
                {entries.map((entry) =>
                    entry.onPick ? (
                        <Entry key={entry.id} $plain>
                            <EntryPick type='button' onClick={entry.onPick}>
                                {entry.icon}
                                <EntryName title={entry.name}>{entry.name}</EntryName>
                            </EntryPick>
                        </Entry>
                    ) : (
                        <Entry key={entry.id}>
                            <EntryStatic>
                                {entry.icon}
                                <EntryName title={entry.name}>{entry.name}</EntryName>
                            </EntryStatic>
                        </Entry>
                    )
                )}
            </List>

            <Commands>
                {actions.map((action) => (
                    <Command key={action.name} type='button' $danger={action.kind === 'danger'} disabled={action.disabled} onClick={action.onClick}>
                        {action.icon}
                        <span>{action.name}</span>
                    </Command>
                ))}
            </Commands>
        </InspectorShell>
    );
};

export default SelectionInspector;
